import { times } from 'ramda';
import type React from 'react';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { Category, type GameV1 } from '@/api/games';
import {
  AnimatedItem,
  AnimatedWrapper,
} from '@/components/animation/AnimatedItem';
import { getGameCover } from '@/helpers/cdn';
import {
  getRandomElementFromList,
  getResultWithProbability,
  randomFromIntervalCrypto,
  randomIntFromInterval,
} from '@/helpers/common';
import {
  amountWithCurrencySymbol,
  CurrencyCode,
  getCurrencyName,
  mapLocaleCurrencyCode,
} from '@/helpers/currency';
import { GENERATED_WINNERS_SUB_NAME } from '@/helpers/gameCategories';
import {
  MAX_MBTC_AMOUNT,
  MAX_WIN_AMOUNT_CENTS,
  MIN_MBTC_AMOUNT,
  MIN_WIN_AMOUNT_CENTS,
  type NewWinnerI,
} from '@/helpers/winner';
import { useGamesList } from '@/hooks/useGamesList';
import { useGamesListLoader } from '@/hooks/useGamesListLoader';
import { useCMSGameCovers } from '@/modules/common/hooks/useCMSGameCovers';
import { userNicknames } from '@/modules/lobby/components/StarWinners/data';
import { useGetCommonStore } from '@/store/commonStore';
import { Spinner } from '@/styles/common';

interface GeneratedWinnersProps {
  winnerItem: React.ComponentType<NewWinnerI>;
  onClick?: () => void;
  itemsPerView?: number;
  intervalToGenerateNew?: number;
  showSpinner?: boolean;
  animationDuration?: number;
}

export const GeneratedWinners: FC<GeneratedWinnersProps> = ({
  winnerItem: WinnerItem,
  onClick,
  itemsPerView = 5,
  intervalToGenerateNew = 4000,
  showSpinner = true,
  animationDuration,
}) => {
  const {
    i18n: { language },
  } = useTranslation('common');

  const { covers } = useCMSGameCovers();
  const useCommonStore = useGetCommonStore();

  const { formatAmountByCurrency, getGamesByCategory } = useCommonStore(
    useShallow((state) => ({
      formatAmountByCurrency: state.formatAmountByCurrency,
      getGamesByCategory: state.getGamesByCategory,
    }))
  );

  const topGamesState = useGamesList(
    'category',
    Category.TOP,
    GENERATED_WINNERS_SUB_NAME
  );

  const loadTopGames = useCallback(() => {
    getGamesByCategory(Category.TOP, {
      pageSize: 50,
      staticAmount: true,
      subName: GENERATED_WINNERS_SUB_NAME,
    });
  }, [getGamesByCategory]);

  useGamesListLoader(topGamesState, loadTopGames);

  const { list: topGamesList, isPending: isTopGamesPending } = topGamesState;

  const { mainColor, blackColor } = useTheme();

  const topGames = useMemo(() => topGamesList?.games || [], [topGamesList]);

  const [winnersSlice, setWinnersSlice] = useState<NewWinnerI[]>([]);

  const localCurrency = useMemo(
    () => mapLocaleCurrencyCode[language],
    [language]
  );

  const usedGamePaths = useMemo(
    () => new Set(winnersSlice.map((w) => w.gameFullPath)),
    [winnersSlice]
  );

  const usedNicknames = useMemo(
    () => new Set(winnersSlice.map((w) => w.nickname)),
    [winnersSlice]
  );

  const generateWinner = useCallback(
    (localCurrency: string): NewWinnerI => {
      const availableGames = topGames.filter(
        (g) => !usedGamePaths.has(g.fullPath)
      );

      const availableNicknames = userNicknames.filter(
        (n) => !usedNicknames.has(n)
      );

      const winGame: GameV1 = getRandomElementFromList(
        availableGames.length ? availableGames : topGames
      );
      const nickname = getRandomElementFromList(
        availableNicknames.length ? availableNicknames : userNicknames
      );
      const coverImage =
        covers?.[winGame.id]?.cover?.url || getGameCover(winGame.id);

      const winCurrency = getResultWithProbability([
        {
          chanceFrom: 0,
          chanceTo: 10,
          option: getCurrencyName(CurrencyCode.BTC),
        },
        {
          chanceFrom: 10,
          chanceTo: 100,
          option: localCurrency,
        },
      ]) as string;

      const moneyAward =
        winCurrency === getCurrencyName(CurrencyCode.BTC)
          ? randomFromIntervalCrypto(MIN_MBTC_AMOUNT, MAX_MBTC_AMOUNT)
          : randomIntFromInterval(MIN_WIN_AMOUNT_CENTS, MAX_WIN_AMOUNT_CENTS);

      const formattedAmount = formatAmountByCurrency(moneyAward, winCurrency);

      const currencyName = getCurrencyName(winCurrency);

      const winAward = amountWithCurrencySymbol(currencyName, formattedAmount);

      return {
        nickname,
        gameTitle: winGame.title,
        humanizedWin: winAward,
        gameTableImagePath: coverImage,
        gameFullPath: winGame.fullPath,
      };
    },
    [topGames, covers, formatAmountByCurrency, usedGamePaths, usedNicknames]
  );

  useEffect(() => {
    if (!winnersSlice.length && topGames.length && localCurrency) {
      setWinnersSlice(times(() => generateWinner(localCurrency), itemsPerView));
    }
  }, [winnersSlice, localCurrency, topGames, generateWinner, itemsPerView]);

  useEffect(() => {
    if (winnersSlice.length && topGames.length) {
      const interval = setInterval(() => {
        const generatedWinner = generateWinner(localCurrency);

        setWinnersSlice((s) => [generatedWinner, ...s.slice(0, -1)]);
      }, intervalToGenerateNew);

      return () => clearInterval(interval);
    }
  }, [
    winnersSlice,
    topGames.length,
    localCurrency,
    generateWinner,
    intervalToGenerateNew,
  ]);

  if (isTopGamesPending) {
    return showSpinner ? (
      <Spinner
        background={blackColor}
        color={mainColor}
        margin="16px auto"
        size={30}
      />
    ) : null;
  }

  return (
    <AnimatedWrapper>
      {winnersSlice.map((winner) => (
        <AnimatedItem
          duration={animationDuration}
          key={`${winner.nickname}-${winner.gameTitle}`}
          layout
        >
          <WinnerItem {...winner} onClick={onClick} />
        </AnimatedItem>
      ))}
    </AnimatedWrapper>
  );
};
